import { useMessages } from '@data/utility/hooks/useMessages';
import { selectThemeColors } from '@src/@data/utility/Utils';
import React, { useMemo } from 'react';
import Select from 'react-select';
import { SlotTicket } from '@src/@data/types/reservations';

export interface TimeslotOption {
  value: Date;
  label: string;
  slotId?: string;
  duration?: number;
  tickets?: SlotTicket[];
}

export interface TimeSelectorProps {
  value: Date | undefined;
  onChange: (option: TimeslotOption | null) => void;
  id?: string;
  timeslots: TimeslotOption[] | undefined;
  isLoading: boolean;
  isDisabled: boolean;
  menuPlacement?: 'auto' | 'top' | 'bottom';
}

export const TimeSelector: React.FC<TimeSelectorProps> = ({
  value,
  onChange,
  id,
  timeslots,
  isLoading,
  isDisabled,
  menuPlacement = 'auto',
}) => {
  const { messages } = useMessages();
  const selectedTimeslot = useMemo(
    () =>
      timeslots?.find((t) => t.value.toString() === value?.toString()) || null,
    [timeslots, value]
  );
  const disabled = isLoading || isDisabled;

  return (
    <Select
      id={id}
      options={timeslots}
      theme={selectThemeColors}
      className="react-select"
      classNamePrefix="select"
      value={selectedTimeslot}
      isClearable={false}
      isSearchable={false}
      onChange={onChange}
      placeholder={
        !disabled && !timeslots?.length
          ? messages('select_no_options')
          : '-- / --'
      }
      styles={{
        menuList: (base) => ({
          ...base,
          padding: 0,
          maxHeight: '150px',
        }),
        menu: (base) => ({ ...base, margin: 0 }),
        indicatorsContainer: (base, props) => ({
          ...base,
          display: props.isDisabled ? 'none' : base.display,
        }),
        singleValue: () => ({}),
        placeholder: () => ({
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: '#8a8a8a !important',
        }),
        valueContainer: (base) => ({
          ...base,
          padding: '8px 12px',
          flexWrap: 'nowrap',
        }),
      }}
      menuPlacement={menuPlacement}
      isDisabled={disabled || !timeslots?.length}
    />
  );
};
